import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { countries } from 'countries-list';
import { createSite, fetchLoginTypes, fetchLanguages } from 'actions/Settings';
import { fetchCompanyList } from 'actions/Company';
import { timeZones } from 'utilities/timeZones';
import { validateEmail } from 'utilities';
import { toastManager } from "components/ToastNotification";
import SelectInput from 'components/SelectInput';
import TextInput from 'components/TextInput';
import NumberInput from "components/NumberInput";
import ButtonContained from 'components/ButtonContained';
import Tooltip from "components/Tooltip";

const CreateFacility = ({ onCreated }) => {
    const dispatch = useDispatch();
    const intl = useIntl();

    const languageRef = useRef(null);
    const temperatureScaleRef = useRef(null);
    const sitePreferredDateFormatRef = useRef(null);
    const alarmEventsRef = useRef(null);
    const alarmTimeRef = useRef(null);
    const offlineExpirationMinutesRef = useRef(null);
    const tokenTimeRef = useRef(null);
    const loginTypesRef = useRef(null);
    const wakeOnMotionRef = useRef(null);
    const timeNotSetFailSecureRef = useRef(null);
    const salesOrderNumberRef = useRef(null);
    const opportunityNumberRef = useRef(null);
    const installModeRef = useRef(null);
    const allowMassUpdatesRef = useRef(null);
    const allowTenantHoursUpdatesRef = useRef(null);
    const allowServiceFobsRef = useRef(null);
    const useFobSpecificScheduleRef = useRef(null);
    const restrictOverlockedEntryRef = useRef(null);
    const ignoreLeadingZeroRef = useRef(null);
    const scheduledUnlockKeysEnabledRef = useRef(null);
    const allowManagerSharesRef = useRef(null);
    const allowTenantSharesRef = useRef(null);
    const disableLocateRef = useRef(null);
    const syncTextEnabledRef = useRef(null);
    const useBlacklistRef = useRef(null);
    const moveOutFeatureRef = useRef(null);
    const hideUserPasswordActivateRef = useRef(null);
    const allowEmployeeRentalRef = useRef(null);
    const geofenceRadiusRef = useRef(null);
    const wirepasVersionRef = useRef(null);
    const preUnlockEnabledRef = useRef(null);
    const rolloutTierRef = useRef(null);
    const onSiteTimeoutRef = useRef(null);
    const networkSSIDRef = useRef(null);
    const networkPassphraseRef = useRef(null);
    const dailyCodesRef = useRef(null);
    const enableDualActivationRef = useRef(null);
    const failedLoginAlertsRef = useRef(null);
    const notificationRef = useRef(null);
    const motionDelayRef = useRef(null);
    const waitAfterNotificationRef = useRef(null);
    const manualNotificationsEnabledRef = useRef(null);
    const haspCutNotificationsEnabledRef = useRef(null);
    const motionNotificationsEnabledRef = useRef(null);
    const unitNotificationsEnabledRef = useRef(null);
    const adminOverlockNotificationsEnabledRef = useRef(null);
    const entryNotificationsEnabledRef = useRef(null);
    const overlockNotificationsEnabledRef = useRef(null);

    const authUser = useSelector((state) => state.auth.authUser);
    const site = useSelector((state) => state.auth.site);
    const siteLanguages = useSelector((state) => state.settings.siteLanguages) || ['english'];
    const companyList = useSelector((state) => state.company.companyList) || [];
    const myFacility = useSelector((state) => state.reports.myFacility);
    const loginTypes = useSelector((state) => state.settings.loginTypes) || [
        'Aws',
        'okta',
        'SiteLink',
        'Microsoft',
        'Noke'
    ];
    
    const [errors, setErrors] = useState({});
    const [companies, setCompanies] = useState([]);
    const [sortedCountries, setSortedCountries] = useState([]);
    const [formValues, setFormValues] = useState(
        {
            name: '',
            city: '',
            companyUUID: '',
            country: '',
            email: '',
            latitude: '0',
            longitude: '0',
            mainUrl: '',
            motion_limit: '3',
            motion_time_minutes: '2',
            notifications_on: 'false',
            entryNotificationsEnabled: 'false',
            overlockNotifications: 'false',
            adminOverlockNotification: 'false',
            manualNotificationsEnabled: 'false',
            haspCutNotificationsEnabled: 'false',
            motionNotificationsEnabled: 'false',
            unitNotificationsEnabled: 'false',
            paymentUrl: '',
            phone: '',
            pms: '',
            postalCode: '',
            state: '',
            salesOrderNumber: '',
            opportunityNumber: '',
            streetAddress: '',
            suite: '',
            timeZone: '',
            language: 'English',
            temperatureScale: 'fahrenheit',
            sitePreferredDateFormat: 'MM/DD/YYYY',
            scheduledUnlockKeysEnabled: 'true',
            syncTextEnabled: 'true',
            useBlacklist: 'true',
            moveOutFeatureEnabled: 'false',
            motionDelay: '',
            waitAfterNotification: '',
            ignoreLeadingZero: 'false',
            wakeOnMotion: 'false',
            allowManagerShares: 'false',
            disableLocate: 'false',
            eula: '',
            offlineExpirationMinutes: '',
            installerAccountsEnabled: 'false',
            timeNotSetFailSecure: 'false',
            tokenTime: 15,
            temperatureNotificationsOn: 'false',
            temperatureMin: '',
            temperatureMax: '',
            allowMassUpdates: 'true',
            siteLoginType: 'noke',
            allowTenantHoursUpdates: 'false',
            hideUserPasswordActivate: 'false',
            allowEmployeeRental: 'true',
            actualRadius: '100',
            wpSiteVersion: '',
            preUnlockEnabled: 'false',
            allowServiceFobs: 'false',
            useFobSpecificSchedule: 'true',
            restrictOverlockedEntry: 'false',
            rolloutTier: 2,
            onSiteTimeout: '0',
            networkSSID: '',
            networkPassphrase: '',
            allowDailyAccessCodes: 'false',
            enableDualActivation: 'true',
            failedLoginNotifications: 'false',
        }
    );

    useEffect(() => {
        dispatch(fetchCompanyList());
        dispatch(fetchLanguages());
        dispatch(fetchLoginTypes());
    }, [dispatch]);

    useEffect(() => {
        const countryList = Object.entries(countries);
        const sortedList = countryList.sort((a, b) => {
            let textA = a[1].name.toUpperCase();
            let textB = b[1].name.toUpperCase();
            return textA < textB ? -1 : textA > textB ? 1 : 0;
        });
        setSortedCountries(sortedList.map(c => ({ value: c[0], name: c[1].name })));
    }, [])

    useEffect(() => {
        if (companyList.length) {
            const processedCompanies = [...companyList]
                .map((company) => ({
                    value: company.uuid,
                    name: company.name,
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
    
            setCompanies(processedCompanies);
        }
    }, [companyList]);

    const saveSetup = () => {
        const newErrors = {};
        if (!formValues.name) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.name = intl.formatMessage({ id: 'notifications.error.blank.facilityName' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.companyUUID) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.companyUUID = intl.formatMessage({ id: 'notifications.error.blank.companyName' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.streetAddress) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.streetAddress = intl.formatMessage({ id: 'notifications.error.blank.streetAddress' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.city) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.city = intl.formatMessage({ id: 'notifications.error.blank.city' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.postalCode) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.postalCode = intl.formatMessage({ id: 'notifications.error.blank.postalCode' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.country) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.country = intl.formatMessage({ id: 'notifications.error.blank.country' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.timeZone) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.timeZone = intl.formatMessage({ id: 'notifications.error.blank.timeZone' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.phone) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.phone = intl.formatMessage({ id: 'notifications.error.blank.phone' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.email) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.email = intl.formatMessage({ id: 'notifications.error.blank.email' });
            setErrors(newErrors);
            return;
        }
        if (!formValues.language) {
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.allDetailsWarning' }));
            newErrors.language = intl.formatMessage({ id: 'notifications.error.blank.language' });
            setErrors(newErrors);
            return;
        }
        if (formValues.country == 'US' && formValues.state.length == 3){
            toastManager.addToast(intl.formatMessage({ id: 'comp.wizard.stateUsaCodeWarning' }));
            newErrors.state = intl.formatMessage({ id: 'comp.wizard.stateUsaCodeWarning' });
            setErrors(newErrors);
            return;
        }
        if (formValues.country == 'CA' && formValues.state.length == 3){
            toastManager.addToast(intl.formatMessage({ id: 'stateCanadaCodeWarning' }));
            newErrors.state = intl.formatMessage({ id: 'stateCanadaCodeWarning' });
            setErrors(newErrors);
            return;
        }
        if (!validateEmail(formValues.email)){
            toastManager.addToast(intl.formatMessage({ id: 'notifications.error.fail.email' }));
            newErrors.email = intl.formatMessage({ id: 'notifications.error.fail.email' });
            setErrors(newErrors);
            return;
        }
        if (Number(formValues.motion_limit) > 99 || Number(formValues.motion_limit) < 2) {
            toastManager.addToast(intl.formatMessage({ id: 'notifications.error.fail.motionLimit' }));
            newErrors.motion_limit = intl.formatMessage({ id: 'notifications.error.fail.motionLimit' });
            setErrors(newErrors);
            return;
        }
        if (Number(formValues.motion_time_minutes) > 99 || Number(formValues.motion_time_minutes) < 1) {
            toastManager.addToast(intl.formatMessage({ id: 'notifications.error.fail.motionTime' }));
            newErrors.motion_time_minutes = intl.formatMessage({ id: 'notifications.error.fail.motionTime' });
            setErrors(newErrors);
            return;
        }
        if (
            formValues.motionDelay !== '' &&
            (Number(formValues.motionDelay) > 60 || Number(formValues.motionDelay) < 30)
        ) {
            toastManager.addToast(intl.formatMessage({ id: 'notifications.error.fail.motionDelay' }));
            newErrors.motionDelay = intl.formatMessage({ id: 'notifications.error.fail.motionDelay' });
            setErrors(newErrors);
            return;
        }
        if (
            formValues.waitAfterNotification !== '' &&
            (Number(formValues.waitAfterNotification) > 30 || Number(formValues.waitAfterNotification) < 5)
        ) {
            toastManager.addToast(intl.formatMessage({ id: 'notifications.error.fail.waitAfterNotification' }));
            newErrors.waitAfterNotification = intl.formatMessage({ id: 'notifications.error.fail.waitAfterNotification' });
            setErrors(newErrors);
            return;
        }
        if (
            formValues.offlineExpirationMinutes !== '' &&
            (Number(formValues.offlineExpirationMinutes) > 14400 ||
                Number(formValues.offlineExpirationMinutes) < 60)
        ) {
            toastManager.addToast(intl.formatMessage({ id: 'notifications.error.fail.offlineExpirationMinutes' }));
            newErrors.offlineExpirationMinutes = intl.formatMessage({ id: 'notifications.error.fail.offlineExpirationMinutes' });
            setErrors(newErrors);
            return;
        }
        if (
            formValues.tokenTime !== '' &&
            (Number(formValues.tokenTime) > 120 || Number(formValues.tokenTime) < 5)
        ) {
            toastManager.addToast(intl.formatMessage({ id: 'notifications.error.fail.tokenTime' }));
            newErrors.tokenTime = intl.formatMessage({ id: 'notifications.error.fail.tokenTime' });
            setErrors(newErrors);
            return;
        }

        setErrors({});

        let newSite = { ...formValues };
        dispatch(createSite(newSite, true, { successMessage: intl.formatMessage({ id: 'notifications.success.add' }) }));
    };

    const handleChange = (id, value) => {
        setFormValues((prev) => ({
            ...prev,
            [id]: value,
            ...(id === 'notifications_on' && { haspCutNotificationsEnabled: String(value === 'true') }),
        }));
    };

    return (
        <div>
            <div className="max-h-[500px] flex flex-col overflow-scroll custom-scrollbar px-8 pt-4">
                <div className="grid grid-cols-2 gap-8 p-2 overflow-visible">
                    <TextInput
                        id="Create-Facility-Name-Input"
                        dataCy="Create-Facility-Name-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.site" })} 
                            ${intl.formatMessage({ id: "form.name" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        hasError={errors.name}
                        errorMessage={errors.name}
                    />
                    <SelectInput
                        id="Create-Facility-Company-Name-Input"
                        dataCy="Create-Facility-Company-Name-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "pages.company.Type" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.companyUUID}
                        onChange={(e) => handleChange('companyUUID', e.target.value)}
                        options={companies}
                        hasError={errors.companyUUID}
                        errorMessage={errors.companyUUID}
                    />
                    <TextInput
                        id="Create-Facility-Street-Input"
                        dataCy="Create-Facility-Street-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.street" })} 
                            ${intl.formatMessage({ id: "form.address" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.streetAddress}
                        onChange={(e) => handleChange('streetAddress', e.target.value)}
                        hasError={errors.streetAddress}
                        errorMessage={errors.streetAddress}
                    />
                    <TextInput
                        id="Create-Facility-Suite-Input"
                        dataCy="Create-Facility-Suite-Input"
                        placeholder={intl.formatMessage({ id: "form.suite" })}
                        value={formValues.suite}
                        onChange={(e) => handleChange('suite', e.target.value)}
                    />
                    <TextInput
                        id="Create-Facility-City-Input"
                        dataCy="Create-Facility-City-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.city" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        hasError={errors.city}
                        errorMessage={errors.city}
                    />
                    <TextInput
                        id="Create-Facility-State-Input"
                        dataCy="Create-Facility-State-Input"
                        placeholder={intl.formatMessage({ id: "form.stateRegion" })}
                        value={formValues.state}
                        onChange={(e) => handleChange('state', e.target.value)}
                        maxLength={3}
                    />
                    <TextInput
                        id="Create-Facility-PostalCode-Input"
                        dataCy="Create-Facility-PostalCode-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.postalCode" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                            `}
                        value={formValues.postalCode}
                        onChange={(e) => handleChange('postalCode', e.target.value)}
                        hasError={errors.postalCode}
                        errorMessage={errors.postalCode}
                    />
                    <SelectInput
                        id="Create-Facility-Country-Input"
                        dataCy="Create-Facility-Country-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.country" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.country}
                        onChange={(e) => handleChange('country', e.target.value)}
                        options={sortedCountries}
                        hasError={errors.country}
                        errorMessage={errors.country}
                    />
                    <SelectInput
                        id="Create-Facility-Timezone-Input"
                        dataCy="Create-Facility-Timezone-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.timeZone" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.timeZone}
                        onChange={(e) => handleChange('timeZone', e.target.value)}
                        options={timeZones.map((zone) => ({ value: zone.name, name: zone.name }))}
                        hasError={errors.timeZone}
                        errorMessage={errors.timeZone}
                    />
                    <TextInput
                        id="Create-Facility-Phone-Input"
                        dataCy="Create-Facility-Phone-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.phone" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        hasError={errors.phone}
                        errorMessage={errors.phone}
                    />
                    <TextInput
                        id="Create-Facility-Email-Input"
                        dataCy="Create-Facility-Email-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: "form.email" })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        hasError={errors.email}
                        errorMessage={errors.email}
                    />
                    <SelectInput
                        ref={languageRef}
                        id="Create-Facility-Language-Input"
                        dataCy="Create-Facility-Language-Input"
                        placeholder={`
                            ${intl.formatMessage({ id: `languages.list` })} 
                            (${intl.formatMessage({ id: "form.required" })})
                        `}
                        value={formValues.language}
                        onChange={(e) => handleChange('language', e.target.value)}
                        options={siteLanguages.map((lang) => ({
                            value: lang,
                            name: lang.charAt(0).toUpperCase() + lang.slice(1)
                        }))}
                        hasError={errors.language}
                        errorMessage={errors.language}
                    />
                    <Tooltip
                        anchorRef={languageRef}
                        text={intl.formatMessage({ id: "form.languageDesc" })}
                    />
                    <SelectInput
                        ref={temperatureScaleRef}
                        id="Create-Facility-Temperature-Scale-Input"
                        dataCy="Create-Facility-Temperature-Scale-Input"
                        placeholder={intl.formatMessage({ id: "comp.table.header.tempDisplay" })}
                        value={formValues.temperatureScale}
                        onChange={(e) => handleChange('temperatureScale', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: "comp.table.header.tempC" }), value: 'celsius' },
                            { name: intl.formatMessage({ id: "comp.table.header.tempF" }), value: 'fahrenheit' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={temperatureScaleRef}
                        text={intl.formatMessage({ id: "form.tempDisplayDesc" })}
                    />
                    <SelectInput
                        ref={sitePreferredDateFormatRef}
                        id="Create-Facility-Date-Format-Input"
                        dataCy="Create-Facility-Date-Format-Input"
                        placeholder={intl.formatMessage({ id: "form.dateFormat" })}
                        value={formValues.sitePreferredDateFormat}
                        onChange={(e) => handleChange('sitePreferredDateFormat', e.target.value)}
                        options={[
                            { name: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
                            { name: 'DD-MM-YYYY', value: 'DD-MM-YYYY' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={sitePreferredDateFormatRef}
                        text={intl.formatMessage({ id: "form.dateFormatTooltip" })}
                    />
                    <TextInput
                        id="Create-Facility-URL-Input"
                        dataCy="Create-Facility-URL-Input"
                        placeholder={intl.formatMessage({ id: "form.url.site" })}
                        value={formValues.mainUrl}
                        onChange={(e) => handleChange('mainUrl', e.target.value)}
                    />
                    <TextInput
                        id="Create-Facility-PaymentURL-Input"
                        dataCy="Create-Facility-PaymentURL-Input"
                        placeholder={intl.formatMessage({ id: "form.url.payment" })}
                        value={formValues.paymentUrl}
                        onChange={(e) => handleChange('paymentUrl', e.target.value)}
                    />
                    {authUser?.permissions?.includes('support_view_all_companies') &&
                        myFacility.companyUUID === '3' && (
                            <TextInput
                                id="Create-Facility-EULA-Input"
                                dataCy="Create-Facility-EULA-Input"
                                placeholder={intl.formatMessage({ id: 'form.url.eula' })}
                                value={formValues.eula}
                                onChange={(e) => handleChange('eula', e.target.value)}
                            />
                    )}
                    <NumberInput
                        ref={alarmEventsRef}
                        id="Create-Facility-Alarm-Events-Input"
                        dataCy="Create-Facility-Alarm-Events-Input"
                        placeholder={intl.formatMessage({ id: 'form.alarmEvents' })}
                        value={formValues.motion_limit}
                        onChange={(e) => handleChange('motion_limit', e.target.value)}
                        hasError={errors.motion_limit}
                        showErrorMessage={errors.motion_limit}
                        errorMessage={errors.motion_limit}
                    />
                    <Tooltip
                        anchorRef={alarmEventsRef}
                        text={intl.formatMessage({ id: "form.alarmEventsDesc" })}
                    />
                    <NumberInput
                        ref={alarmTimeRef}
                        id="Create-Facility-Alarm-Time-Input"
                        dataCy="Create-Facility-Alarm-Time-Input"
                        placeholder={intl.formatMessage({ id: `form.alarmTime` })}
                        value={formValues.motion_time_minutes}
                        onChange={(e) => handleChange('motion_time_minutes', e.target.value)}
                        hasError={errors.motion_time_minutes}
                        showErrorMessage={errors.motion_time_minutes}
                        errorMessage={errors.motion_time_minutes}
                    />
                    <Tooltip
                        anchorRef={alarmTimeRef}
                        text={intl.formatMessage({ id: "form.alarmTimeDesc" })}
                    />
                    <NumberInput
                        ref={offlineExpirationMinutesRef}
                        id="Create-Facility-Offline-Expiration-Input"
                        dataCy="Create-Facility-Offline-Expiration-Input"
                        placeholder={`${intl.formatMessage({ id: "form.offlineExpirationMinutes" })} (60-1440)`}
                        value={formValues.offlineExpirationMinutes}
                        onChange={(e) => handleChange('offlineExpirationMinutes', e.target.value)}
                        min={60}
                        max={1440}
                        hasError={errors.offlineExpirationMinutes}
                        showErrorMessage={errors.offlineExpirationMinutes}
                        errorMessage={errors.offlineExpirationMinutes}
                    />
                    <Tooltip
                        anchorRef={offlineExpirationMinutesRef}
                        text={intl.formatMessage({ id: "form.offlineExpirationMinutesDesc" })}
                    />
                    <NumberInput
                        ref={tokenTimeRef}
                        id="Create-Facility-Token-Time-Input"
                        dataCy="Create-Facility-Token-Time-Input"
                        placeholder={`${intl.formatMessage({ id: "form.tokenTime" })} (5-120)`}
                        onChange={(e) => handleChange("tokenTime", e.target.value)}
                        value={formValues.tokenTime}
                        min={5}
                        max={120}
                        hasError={errors.tokenTime}
                        showErrorMessage={errors.tokenTime}
                        errorMessage={errors.tokenTime}
                    />
                    <Tooltip
                        anchorRef={tokenTimeRef}
                        text={intl.formatMessage({ id: "form.tokenTimeDesc" })}
                    />
                    {loginTypes && <>
                        <SelectInput
                            ref={loginTypesRef}
                            id="Create-Facility-Login-Type-Input"
                            dataCy="Create-Facility-Login-Type-Input"
                            placeholder={intl.formatMessage({ id: `form.loginType` })}
                            value={formValues.siteLoginType}
                            onChange={(e) => handleChange('siteLoginType', e.target.value)}
                            options={loginTypes.map((type) => ({
                                name: type.charAt(0).toUpperCase() + type.slice(1),
                                value: type
                            }))}
                        />
                        <Tooltip
                            anchorRef={loginTypesRef}
                            text={intl.formatMessage({ id: "form.loginTypeDesc" })}
                        />
                    </>}
                    {authUser?.featureflagAssignments?.includes('wake_on_motion') && <>
                        <SelectInput
                            ref={wakeOnMotionRef}
                            id="Create-Facility-Wake-On-Motion-Input"
                            dataCy="Create-Facility-Wake-On-Motion-Input"
                            placeholder={intl.formatMessage({ id: `form.wakeOnMotion` })}
                            value={formValues.wakeOnMotion}
                            onChange={(e) => handleChange('wakeOnMotion', e.target.value)}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={wakeOnMotionRef}
                            text={intl.formatMessage({ id: "form.wakeOnMotionDesc" })}
                        />
                    </>}
                    {authUser?.featureflagAssignments?.includes('time_not_set_fail_secure') && <>
                        <SelectInput
                            ref={timeNotSetFailSecureRef}
                            id="Create-Facility-Time-Not-Set-Fail-Secure-Input"
                            dataCy="Create-Facility-Time-Not-Set-Fail-Secure-Input"
                            placeholder={intl.formatMessage({ id: `form.timeNotSetFailSecure` })}
                            value={formValues.timeNotSetFailSecure}
                            onChange={(e) => handleChange('timeNotSetFailSecure', e.target.value)}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={timeNotSetFailSecureRef}
                            text={intl.formatMessage({ id: "form.timeNotSetFailSecureDesc" })}
                        />
                    </>}
                    {authUser.permissions.includes('support_view_all_companies') && <>
                        <NumberInput
                            ref={salesOrderNumberRef}
                            id="Create-Facility-Sales-Order-Input"
                            dataCy="Create-Facility-Sales-Order-Input"
                            placeholder={intl.formatMessage({ id: `comp.salesOrderNumber` })}
                            value={formValues.salesOrderNumber}
                            onChange={(e) => handleChange('salesOrderNumber', e.target.value)}
                        />
                        <Tooltip
                            anchorRef={salesOrderNumberRef}
                            text={intl.formatMessage({ id: "comp.salesOrderNumberDesc" })}
                        />
                    </>}
                    {authUser.permissions.includes('support_view_all_companies') && <>
                        <NumberInput
                            ref={opportunityNumberRef}
                            id="Create-Facility-Opportunity-Number-Input"
                            dataCy="Create-Facility-Opportunity-Number-Input"
                            placeholder={intl.formatMessage({ id: `comp.opportunityNumber` })}
                            value={formValues.opportunityNumber}
                            onChange={(e) => handleChange('opportunityNumber', e.target.value)}
                        />
                        <Tooltip
                            anchorRef={opportunityNumberRef}
                            text={intl.formatMessage({ id: "comp.opportunityNumberDesc" })}
                        />
                    </>}
                    {authUser.permissions.includes('support_view_all_companies') && <>
                        <SelectInput
                            ref={installModeRef}
                            id="Create-Facility-Installer-Accounts-Input"
                            dataCy="Create-Facility-Installer-Accounts-Input"
                            placeholder={intl.formatMessage({ id: `comp.installMode` })}
                            value={formValues.installerAccountsEnabled}
                            onChange={(e) => handleChange('installerAccountsEnabled', e.target.value)}
                            options={[
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' },
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={installModeRef}
                            text={intl.formatMessage({ id: "comp.installerModeDesc" })}
                        />
                    </>}
                    {authUser.permissions.includes('support_view_all_companies') && <>
                        <SelectInput
                            ref={allowMassUpdatesRef}
                            id="Create-Facility-Allow-Updates-Input"
                            dataCy="Create-Facility-Allow-Updates-Input"
                            placeholder={intl.formatMessage({ id: `form.allowMassUpdates` })}
                            value={formValues.allowMassUpdates}
                            onChange={(e) => handleChange('allowMassUpdates', e.target.value)}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={allowMassUpdatesRef}
                            text={intl.formatMessage({ id: "form.allowMassUpdatesDesc" })}
                        />
                    </>}
                    <SelectInput
                        ref={allowTenantHoursUpdatesRef}
                        id="Create-Facility-Allow-Tenant-Hours-Updates-Input"
                        dataCy="Create-Facility-Allow-Tenant-Hours-Updates-Input"
                        value={formValues.allowTenantHoursUpdates}
                        placeholder={intl.formatMessage({ id: `form.allowTenantHoursUpdates` })}
                        onChange={(e) => handleChange('allowTenantHoursUpdates', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={allowTenantHoursUpdatesRef}
                        text={intl.formatMessage({ id: "form.allowTenantHoursUpdatesDesc" })}
                    />
                    <SelectInput
                        ref={allowServiceFobsRef}
                        id="Create-Facility-Allow-Service-Fobs-Input"
                        dataCy="Create-Facility-Allow-Service-Fobs-Input"
                        placeholder={intl.formatMessage({ id: `pages.fobs.allowServiceFobs` })}
                        value={formValues.allowServiceFobs}
                        onChange={(e) => handleChange('allowServiceFobs', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={allowServiceFobsRef}
                        text={intl.formatMessage({ id: "pages.fobs.allowServiceFobs.desc" })}
                    />
                    {/* False is true and true is false here because this setting was initially set up to reflect fobs 
                    following the fob schedule, but was later modified to reflect the fobs following the user schedule. 
                    Rather than update all of the places in the backend code it was easier to simply invert the setting 
                    on front end to reflect accurately. */}
                    <SelectInput
                        ref={useFobSpecificScheduleRef}
                        id="Create-Facility-Use-Fob-Schedule-Input"
                        dataCy="Create-Facility-Use-Fob-Schedule-Input"
                        value={formValues.useFobSpecificSchedule}
                        onChange={(e) => handleChange('useFobSpecificSchedule', e.target.value)}
                        placeholder={intl.formatMessage({ id: `pages.fobs.useFobSpecificSchedule` })}
                        options={[
                            { name: intl.formatMessage({ id: 'form.false' }), value: 'true' },
                            { name: intl.formatMessage({ id: 'form.true' }), value: 'false' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={useFobSpecificScheduleRef}
                        text={intl.formatMessage({ id: "pages.fobs.useFobSpecificSchedule.desc" })}
                    />
                    <SelectInput
                        ref={restrictOverlockedEntryRef}
                        id="Create-Facility-Restrict-Overlocked-Entry-Input"
                        dataCy="Create-Facility-Restrict-Overlocked-Entry-Input"
                        placeholder={intl.formatMessage({ id: 'form.restrictOverlockedEntry' })}
                        value={formValues.restrictOverlockedEntry}
                        onChange={(e) => handleChange('restrictOverlockedEntry', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={restrictOverlockedEntryRef}
                        text={intl.formatMessage({ id: "form.restrictOverlockedEntryDesc" })}
                    />
                    {authUser?.featureflagAssignments?.includes('ignore_leading_zero') && <>
                        <SelectInput
                            ref={ignoreLeadingZeroRef}
                            id="Create-Facility-Ignore-Access-Zeros-Input"
                            dataCy="Create-Facility-Ignore-Access-Zeros-Input"
                            placeholder={intl.formatMessage({ id: `form.ignoreLeadingZero` })}
                            value={formValues.ignoreAccessCodesLeadingZeros}
                            onChange={(e) => handleChange('ignoreAccessCodesLeadingZeros', e.target.value)}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={ignoreLeadingZeroRef}
                            text={intl.formatMessage({ id: "form.ignoreLeadingZeroDesc" })}
                        />
                    </>}
                    {authUser?.permissions?.includes('support_view_gateways') && <>
                        <SelectInput
                            ref={scheduledUnlockKeysEnabledRef}
                            id="Create-Facility-Unlock-Keys-Enabled-Input"
                            dataCy="Create-Facility-Unlock-Keys-Enabled-Input"
                            value={formValues.scheduledUnlockKeysEnabled}
                            onChange={(e) => handleChange('scheduledUnlockKeysEnabled', e.target.value)}
                            placeholder={intl.formatMessage({ id: 'form.scheduledUnlockKeysEnabled' })}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={scheduledUnlockKeysEnabledRef}
                            text={intl.formatMessage({ id: "form.scheduleKeysDesc" })}
                        />
                    </>}
                    {authUser?.permissions?.includes('open_manager_shares') && <>
                        <SelectInput
                            ref={allowManagerSharesRef}
                            id="Create-Facility-Allow-Manager-Shares-Input"
                            dataCy="Create-Facility-Allow-Manager-Shares-Input"
                            value={formValues.allowManagerShares}
                            onChange={(e) => handleChange('allowManagerShares', e.target.value)}
                            placeholder={intl.formatMessage({ id: 'form.allowManagerShares' })}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={allowManagerSharesRef}
                            text={intl.formatMessage({ id: "form.allowManagerSharesDesc" })}
                        />
                    </>}
                    {authUser?.permissions?.includes("open_manager_shares") && <>
                        <SelectInput
                            ref={allowTenantSharesRef}
                            id="Create-Facility-Allow-Tenant-Shares-Input"
                            dataCy="Create-Facility-Allow-Tenant-Shares-Input"
                            placeholder={intl.formatMessage({ id: 'form.allowTenantShares' })}
                            value={formValues.allowTenantShares}
                            onChange={(e) => handleChange('allowTenantShares', e.target.value)}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={allowTenantSharesRef}
                            text={intl.formatMessage({ id: "form.allowTenantSharesDesc" })}
                        />
                    </>}
                    <SelectInput
                        ref={disableLocateRef}
                        id="Create-Facility-Disable-Locate-Input"
                        dataCy="Create-Facility-Disable-Locate-Input"
                        placeholder={intl.formatMessage({ id: 'form.disableLocate' })}
                        value={formValues.disableLocate}
                        onChange={(e) => handleChange('disableLocate', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'comp.form.no' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'comp.form.yes' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={disableLocateRef}
                        text={intl.formatMessage({ id: "form.disableLocateDesc" })}
                    />
                    <SelectInput
                        ref={syncTextEnabledRef}
                        id="Create-Facility-Sync-Text-Input"
                        dataCy="Create-Facility-Sync-Text-Input"
                        placeholder={intl.formatMessage({ id: 'form.syncTextEnabled' })}
                        value={formValues.syncTextEnabled}
                        onChange={(e) => handleChange('syncTextEnabled', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={syncTextEnabledRef}
                        text={intl.formatMessage({ id: "form.syncTextDesc" })}
                    />
                    <SelectInput
                        ref={useBlacklistRef}
                        id="Create-Facility-Use-Blacklist-Input"
                        dataCy="Create-Facility-Use-Blacklist-Input"
                        placeholder={intl.formatMessage({ id: 'form.useBlacklist' })}
                        value={formValues.useBlacklist}
                        onChange={(e) => handleChange('useBlacklist', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={useBlacklistRef}
                        text={intl.formatMessage({ id: "form.useBlacklistDesc" })}
                    />
                    <SelectInput
                        ref={moveOutFeatureRef}
                        id="Create-Facility-Move-Out-Input"
                        dataCy="Create-Facility-Move-Out-Input"
                        placeholder={intl.formatMessage({ id: 'form.moveOutFeature' })}
                        value={formValues.moveOutFeatureEnabled}
                        onChange={(e) => handleChange('moveOutFeatureEnabled', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={moveOutFeatureRef}
                        text={intl.formatMessage({ id: "form.moveOutFeatureDesc" })}
                    />
                    <SelectInput
                        ref={hideUserPasswordActivateRef}
                        id="Create-Facility-Hide-User-P-A-Input"
                        dataCy="Create-Facility-Hide-User-P-A-Input"
                        placeholder={intl.formatMessage({ id: 'form.hideUserPasswordActivate' })}
                        value={formValues.hideUserPasswordActivate}
                        onChange={(e) => handleChange('hideUserPasswordActivate', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={hideUserPasswordActivateRef}
                        text={intl.formatMessage({ id: "form.hideUserPasswordActivateDesc" })}
                    />
                    <SelectInput
                        ref={allowEmployeeRentalRef}
                        id="Create-Facility-Allow-Employee-Rental-Input"
                        dataCy="Create-Facility-Allow-Employee-Rental-Input"
                        placeholder={intl.formatMessage({ id: 'form.allowEmployeeRental' })}
                        value={formValues.allowEmployeeRental}
                        onChange={(e) => handleChange('allowEmployeeRental', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={allowEmployeeRentalRef}
                        text={intl.formatMessage({ id: "form.allowEmployeeRental.desc" })}
                    />
                    {authUser?.featureflagAssignments?.includes('adjust_geofence') && <>
                        <NumberInput
                            ref={geofenceRadiusRef}
                            id="Create-Facility-Geofence-Radius-Input"
                            dataCy="Create-Facility-Geofence-Radius-Input"
                            placeholder={`${intl.formatMessage({ id: `form.geofenceRadius` })} (100-500)`}
                            value={formValues.actualRadius}
                            onChange={(e) => handleChange('actualRadius', e.target.value)}
                            min={100}
                            max={500}
                        />
                        <Tooltip
                            anchorRef={geofenceRadiusRef}
                            text={intl.formatMessage({ id: "form.geofenceDesc" })}
                        />
                    </>}
                    {authUser?.permissions?.includes('support_view_all_companies') && <>
                        <TextInput
                            ref={wirepasVersionRef}
                            id="Create-Facility-Wirepas-Version-Input"
                            dataCy="Create-Facility-Wirepas-Version-Input"
                            placeholder={intl.formatMessage({ id: `form.wirepasVersion` })}
                            value={formValues.wpSiteVersion}
                            onChange={(e) => handleChange('wpSiteVersion', e.target.value)}
                        />
                        <Tooltip
                            anchorRef={wirepasVersionRef}
                            text={intl.formatMessage({ id: "form.wirepasDesc" })}
                        />
                    </>}
                    {authUser?.featureflagAssignments?.includes('preunlock_enabled') && <>
                        <SelectInput
                            ref={preUnlockEnabledRef}
                            id="Create-Facility-Preunlock-Enabled-Input"
                            dataCy="Create-Facility-Preunlock-Enabled-Input"
                            placeholder={intl.formatMessage({ id: `form.preunlockEnabled` })}
                            value={formValues.preUnlockEnabled}
                            onChange={(e) => handleChange('preUnlockEnabled', e.target.value)}
                            options={[
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' },
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={preUnlockEnabledRef}
                            text={intl.formatMessage({ id: "form.preunlockgDesc" })}
                        />
                    </>}
                    {authUser?.permissions?.includes('support_high_level') && <>
                        <SelectInput
                            ref={rolloutTierRef}
                            id="Create-Facility-Rollout-Tier-Input"
                            dataCy="Create-Facility-Rollout-Tier-Input"
                            placeholder={intl.formatMessage({ id: `comp.rolloutTier` })}
                            value={formValues.rolloutTier}
                            onChange={(e) => handleChange('rolloutTier', e.target.value)}
                            options={[
                                { name: '1', value: 1 },
                                { name: '2', value: 2 },
                                { name: '3', value: 3 }
                            ]}
                        />
                        <Tooltip
                            anchorRef={rolloutTierRef}
                            text={intl.formatMessage({ id: "comp.rolloutTierDesc" })}
                        />
                    </>}
                    <NumberInput
                        ref={onSiteTimeoutRef}
                        id="Create-Facility-Onsite-Timeout-Input"
                        dataCy="Create-Facility-Onsite-Timeout-Input"
                        placeholder={intl.formatMessage({ id: 'comp.onSiteTimeout' })}
                        value={formValues.onSiteTimeout}
                        onChange={(e) => handleChange('onSiteTimeout', e.target.value)}
                    />
                    <Tooltip
                        anchorRef={onSiteTimeoutRef}
                        text={intl.formatMessage({ id: "comp.onSiteTimeoutDesc" })}
                    />
                    <TextInput
                        ref={networkSSIDRef}
                        id="Create-Facility-Network-SSID-Input"
                        dataCy="Create-Facility-Network-SSID-Input"
                        placeholder={intl.formatMessage({id: `comp.networkSSID` })}
                        value={formValues.networkSSID}
                        onChange={(e) => handleChange('networkSSID', e.target.value)}
                    />
                    <Tooltip
                        anchorRef={networkSSIDRef}
                        text={intl.formatMessage({ id: "comp.networkSSIDDesc" })}
                    />
                    <TextInput
                        ref={networkPassphraseRef}
                        id="Create-Facility-Network-Passphrase-Input"
                        dataCy="Create-Facility-Network-Passphrase-Input"
                        placeholder={intl.formatMessage({ id: `comp.networkPassphrase` })}
                        value={formValues.networkPassphrase}
                        onChange={(e) => handleChange('networkPassphrase', e.target.value)}
                    />
                    <Tooltip
                        anchorRef={networkPassphraseRef}
                        text={intl.formatMessage({ id: "comp.networkPassphraseDesc" })}
                    />
                    {authUser?.permissions?.includes('manage_access_codes') && <>
                        <SelectInput
                            ref={dailyCodesRef}
                            id="Create-Facility-Daily-Codes-Input"
                            dataCy="Create-Facility-Daily-Codes-Input"
                            value={formValues.allowDailyAccessCodes}
                            onChange={(e) => handleChange('allowDailyAccessCodes', e.target.value)}
                            placeholder={intl.formatMessage({ id: `pages.dashboard.dailyCodes` })}
                            options={[
                                { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                                { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                            ]}
                        />
                        <Tooltip
                            anchorRef={dailyCodesRef}
                            text={intl.formatMessage({ id: "pages.dashboard.dailyCodesDesc" })}
                        />
                    </>}
                    <SelectInput
                        ref={enableDualActivationRef}
                        id="Create-Facility-Enable-Dual-Activation-Input"
                        dataCy="Create-Facility-Enable-Dual-Activation-Input"
                        placeholder={intl.formatMessage({ id: 'form.enableDualActivation' })}
                        value={formValues.enableDualActivation}
                        onChange={(e) => handleChange('enableDualActivation', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={enableDualActivationRef}
                        text={intl.formatMessage({ id: "form.enableDualActivationDesc" })}
                    />
                    <SelectInput
                        ref={failedLoginAlertsRef}
                        id="Create-Facility-Failed-Login-Alert-Input"
                        dataCy="Create-Facility-Failed-Login-Alert-Input"
                        placeholder={intl.formatMessage({ id: 'form.failedLoginAlert' })}
                        value={formValues.failedLoginNotifications}
                        onChange={(e) => handleChange('failedLoginNotifications', e.target.value)}
                        options={[
                            { name: intl.formatMessage({ id: 'form.off' }), value: 'false' },
                            { name: intl.formatMessage({ id: 'form.on' }), value: 'true' }
                        ]}
                    />
                    <Tooltip
                        anchorRef={failedLoginAlertsRef}
                        text={intl.formatMessage({ id: "form.failedLoginAlertDesc" })}
                    />
                    <SelectInput
                        ref={notificationRef}
                        id="Create-Facility-Notifications-On-Input"
                        dataCy="Create-Facility-Notifications-On-Input"
                        placeholder={intl.formatMessage({ id: `comp.button.notifications` })}
                        value={formValues.notifications_on}
                        onChange={(e) => handleChange('notifications_on', e.target.value)}
                        options={[
                            {
                                name: intl.formatMessage({ id: 'form.off' }),
                                value: 'false'
                            },
                            {
                                name: intl.formatMessage({ id: 'form.on' }),
                                value: 'true'
                            }
                        ]}
                    />
                    <Tooltip
                        anchorRef={notificationRef}
                        text={intl.formatMessage({ id: "form.siteNotificationsDesc" })}
                    />
                </div>
                <div>
                    {formValues && formValues.notifications_on && formValues.notifications_on === 'true' && (
                        <div className="p-2">
                            {/* Admin Notifications */}
                            <div className="tw-paragraph-reg font-semibold pl-2 mb-4 uppercase">
                                {intl.formatMessage({ id: 'role.noke_admin' })}{' '}
                                {intl.formatMessage({ id: 'pages.home.notifications' })}
                            </div>
                            <div className="grid grid-cols-2 gap-8 overflow-visible">
                                {authUser?.featureflagAssignments?.includes("motion_notification_shutoff") && <>
                                    <NumberInput
                                        ref={motionDelayRef}
                                        id="Create-Facility-Motion-Delay-Input"
                                        dataCy="Create-Facility-Motion-Delay-Input"
                                        placeholder={`${intl.formatMessage({ id: `form.motionDelay` })} (30-60)`}
                                        value={formValues.motionDelay}
                                        onChange={(e) => handleChange('motionDelay', e.target.value)}
                                        min={30}
                                        max={60}
                                        hasError={errors.motionDelay}
                                        showErrorMessage={errors.motionDelay}
                                        errorMessage={errors.motionDelay}
                                    />
                                    <Tooltip
                                        anchorRef={motionDelayRef}
                                        text={intl.formatMessage({ id: "form.motionDelayDesc" })}
                                    />
                                </>}
                                {authUser?.featureflagAssignments?.includes("motion_notification_shutoff") && <>
                                    <NumberInput
                                        ref={waitAfterNotificationRef}
                                        id="Create-Facility-Wait-After-Input"
                                        dataCy="Create-Facility-Wait-After-Input"
                                        placeholder={`${intl.formatMessage({ id: `form.waitAfterNotification` })} (5-30)`}
                                        value={formValues.waitAfterNotification}
                                        onChange={(e) => handleChange('waitAfterNotification', e.target.value)}
                                        min={5}
                                        max={30}
                                        hasError={errors.waitAfterNotification}
                                        showErrorMessage={errors.waitAfterNotification}
                                        errorMessage={errors.waitAfterNotification}
                                    />
                                    <Tooltip
                                        anchorRef={waitAfterNotificationRef}
                                        text={intl.formatMessage({ id: "form.waitAfterNotificationDesc" })}
                                    />
                                </>}
                                <SelectInput
                                    ref={manualNotificationsEnabledRef}
                                    id="Create-Facility-Manual-Notifications-Input"
                                    dataCy="Create-Facility-Manual-Notifications-Input"
                                    placeholder={intl.formatMessage({ id: `form.manualNotificationsEnabled` })}
                                    options={[
                                        {
                                            name: intl.formatMessage({ id: 'form.off' }),
                                            value: 'false'
                                        },
                                        {
                                            name: intl.formatMessage({ id: 'form.on' }),
                                            value: 'true'
                                        }
                                    ]}
                                    value={formValues.manualNotificationsEnabled}
                                    onChange={(e) => handleChange('manualNotificationsEnabled', e.target.value)}
                                />
                                <Tooltip
                                    anchorRef={manualNotificationsEnabledRef}
                                    text={intl.formatMessage({ id: "form.manualNotificationsDesc" })}
                                />
                                {site?.consolidated3AInfinite === "true" && (
                                    <>
                                        <TextInput
                                            ref={haspCutNotificationsEnabledRef}
                                            id="Create-Facility-Hasp-Cut-Notifications-Input"
                                            dataCy="Create-Facility-Hasp-Cut-Notifications-Input"
                                            placeholder={'Cut Hasp Notifications'}
                                            isReadOnly={true}
                                            value={formValues.haspCutNotificationsEnabled === 'true' ? intl.formatMessage({ id: 'form.on' }) : intl.formatMessage({ id: 'form.off' })}
                                        />
                                        <Tooltip
                                            anchorRef={haspCutNotificationsEnabledRef}
                                            text={'Alerts managers when a lock hasp cut is detected. This setting is enabled by default when site notifications are enabled and cannot be disabled.'}
                                        />
                                    </>
                                )}
                                <SelectInput
                                    ref={motionNotificationsEnabledRef}
                                    id="Create-Facility-Motion-Notifications-Input"
                                    dataCy="Create-Facility-Motion-Notifications-Input"
                                    placeholder={intl.formatMessage({ id: `form.motionNotificationsEnabled` })}
                                    options={[
                                        {
                                            name: intl.formatMessage({ id: 'form.off' }),
                                            value: 'false'
                                        },
                                        {
                                            name: intl.formatMessage({ id: 'form.on' }),
                                            value: 'true'
                                        }
                                    ]}
                                    value={formValues.motionNotificationsEnabled}
                                    onChange={(e) => handleChange('motionNotificationsEnabled', e.target.value)}
                                />
                                <Tooltip
                                    anchorRef={motionNotificationsEnabledRef}
                                    text={intl.formatMessage({ id: "form.motionNotificationsDesc" })}
                                />
                                <SelectInput
                                    ref={unitNotificationsEnabledRef}
                                    id="Create-Facility-Unit-Notifications-Input"
                                    dataCy="Create-Facility-Unit-Notifications-Input"
                                    placeholder={intl.formatMessage({ id: `form.unitNotificationsEnabled` })}
                                    options={[
                                        {
                                            name: intl.formatMessage({ id: 'form.off' }),
                                            value: 'false'
                                        },
                                        {
                                            name: intl.formatMessage({ id: 'form.on' }),
                                            value: 'true'
                                        }
                                    ]}
                                    value={formValues.unitNotificationsEnabled}
                                    onChange={(e) => handleChange('unitNotificationsEnabled', e.target.value)}
                                />
                                <Tooltip
                                    anchorRef={unitNotificationsEnabledRef}
                                    text={intl.formatMessage({ id: "form.unitNotificationsDesc" })}
                                />
                                <SelectInput
                                    ref={adminOverlockNotificationsEnabledRef}
                                    id="Create-Facility-Overlock-Notifications-Input"
                                    dataCy="Create-Facility-Overlock-Notifications-Input"
                                    placeholder={intl.formatMessage({ id: `form.adminOverlockNotificationsEnabled` })}
                                    options={[
                                        {
                                            name: intl.formatMessage({ id: 'form.off' }),
                                            value: 'false'
                                        },
                                        {
                                            name: intl.formatMessage({ id: 'form.on' }),
                                            value: 'true'
                                        }
                                    ]}
                                    value={formValues.adminOverlockNotification}
                                    onChange={(e) => handleChange('adminOverlockNotification', e.target.value)}
                                />
                                <Tooltip
                                    anchorRef={adminOverlockNotificationsEnabledRef}
                                    text={intl.formatMessage({ id: "form.adminOverlockNotificationsEnabledDesc" })}
                                />
                            </div>

                            {/* Tenant Notifications */}
                            <div className="tw-paragraph-reg font-semibold pl-2 my-4 uppercase">
                                {intl.formatMessage({ id: 'role.client' })}{' '}
                                {intl.formatMessage({ id: 'pages.home.notifications' })}
                            </div>
                            <div className="grid grid-cols-2 gap-8 overflow-visible">
                                <SelectInput
                                    ref={entryNotificationsEnabledRef}
                                    id="Create-Facility-Entry-Notifications-Input"
                                    dataCy="Create-Facility-Entry-Notifications-Input"
                                    placeholder={intl.formatMessage({ id: `form.entryNotificationsEnabled`})}
                                    options={[
                                        {
                                            name: intl.formatMessage({ id: 'form.off' }),
                                            value: 'false'
                                        },
                                        {
                                            name: intl.formatMessage({ id: 'form.on' }),
                                            value: 'true'
                                        }
                                    ]}
                                    value={formValues.entryNotificationsEnabled}
                                    onChange={(e) => handleChange('entryNotificationsEnabled', e.target.value)}
                                />
                                <Tooltip
                                    anchorRef={entryNotificationsEnabledRef}
                                    text={intl.formatMessage({ id: "form.entryNotificationsDesc" })}
                                />
                                <SelectInput
                                    ref={overlockNotificationsEnabledRef}
                                    id="Create-Facility-Overlock-Notifications-Input"
                                    dataCy="Create-Facility-Overlock-Notifications-Input"
                                    placeholder={intl.formatMessage({
                                        id: `form.overlockNotificationsEnabled`
                                    })}
                                    options={[
                                        {
                                            name: intl.formatMessage({
                                                id: 'form.off'
                                            }),
                                            value: 'false'
                                        },
                                        {
                                            name: intl.formatMessage({
                                                id: 'form.on'
                                            }),
                                            value: 'true'
                                        }
                                    ]}
                                    value={formValues.overlockNotifications || ''}
                                    onChange={(e) => handleChange('overlockNotifications', e?.target.value)}
                                    name={'overlockNotifications'}
                                />
                                <Tooltip
                                    anchorRef={overlockNotificationsEnabledRef}
                                    text={intl.formatMessage({ id: "form.overlockNotificationsDesc" })}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end px-8 pt-4">
                <ButtonContained
                    id="Create-Facility-Page-Create-Button"
                    dataCy="Create-Facility-Page-Create-Button"
                    text={intl.formatMessage({ id: 'comp.button.create' })}
                    type="default"
                    onClick={() => saveSetup()}
                />
            </div>
        </div>
    );
};

export default CreateFacility;
